import { Component, inject, Input, Output, EventEmitter } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { MsalService } from '@azure/msal-angular'
import { AuthenticationResult } from '@azure/msal-browser'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ToastrService } from 'ngx-toastr'

@Component({
   selector: 'meeting-scheduler',
   templateUrl: './meeting-scheduler.component.html',
   styleUrls: ['./meeting-scheduler.component.scss'],
   standalone: false,
})
export class MeetingSchedulerComponent {
   isSignedIn = false
   accessToken: string | null = null
   result: any

   @Input() subject: string = ''
   @Input() inModal: boolean = true
   @Input() allowedStartDate?: string
   @Input() allowedEndDate?: string
   @Output() meetingCreated = new EventEmitter<any>()
   @Input() captainUser: any | null = null
   meetingDate: Date | null = null
   startTime: string = ''
   availableTimes: { label: string; value: string }[] = []
   loading = false

   minCalendarDate: Date | null = null
   maxCalendarDate: Date | null = null

   timeZoneOptions = [
      { label: 'Eastern Time (US & Canada)', value: 'Eastern Standard Time' },
      { label: 'Central Time (US & Canada)', value: 'Central Standard Time' },
      { label: 'Mountain Time (US & Canada)', value: 'Mountain Standard Time' },
      { label: 'Pacific Time (US & Canada)', value: 'Pacific Standard Time' },
      { label: 'Hawaii Time', value: 'Hawaiian Standard Time' },
   ]
   selectedTimeZone = ''

   private windowsToIanaMap: Record<string, string> = {
      'Eastern Standard Time': 'America/New_York',
      'Central Standard Time': 'America/Chicago',
      'Mountain Standard Time': 'America/Denver',
      'Pacific Standard Time': 'America/Los_Angeles',
      'Hawaiian Standard Time': 'Pacific/Honolulu',
   }

   private ianaToWindowsMap: Record<string, string> = {
      'America/New_York': 'Eastern Standard Time',
      'America/Chicago': 'Central Standard Time',
      'America/Denver': 'Mountain Standard Time',
      'America/Los_Angeles': 'Pacific Standard Time',
      'Pacific/Honolulu': 'Hawaiian Standard Time',
   }

   duration: number = 45
   durationOptions = [
      // { label: '15 minutes', value: 15 },
      // { label: '30 minutes', value: 30 },
      { label: '45 minutes', value: 45 },
      // { label: '1 hour', value: 60 },
   ]

   dateFilter = (date: Date): boolean => {
      const min = this.minDate
      const max = this.maxDate
      return (!min || date >= min) && (!max || date <= max)
   }

   userSuggestions: any[] = []
   attendeeUsers: any[] = []
   currentUser: any | null = null

   authManager = inject(AuthManager)

   constructor(
      private msal: MsalService,
      private http: HttpClient,
      private toastr: ToastrService
   ) {}

   ngOnInit(): void {
      this.authManager.getCurrentUser().subscribe((user) => {
         this.currentUser = user
      })

      if (this.allowedStartDate) {
         const d = new Date(this.allowedStartDate)
         this.minCalendarDate = isNaN(d.getTime()) ? null : d
      }

      if (this.allowedEndDate) {
         const d = new Date(this.allowedEndDate)
         this.maxCalendarDate = isNaN(d.getTime()) ? null : d
      }

      // detect browser's timezone
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (this.ianaToWindowsMap[tz]) {
         this.selectedTimeZone = this.ianaToWindowsMap[tz]
      }
   }

   // PrimeNG AutoComplete calls this as the user types
   onUserComplete(event: { query: string }) {
      const q = (event.query || '').trim()

      if (!q || q.length < 2) {
         this.userSuggestions = []
         return
      }

      const account = this.msal.instance.getActiveAccount()
      if (!account) return

      this.msal
         .acquireTokenSilent({
            account,
            scopes: ['User.ReadBasic.All'], // only need basic profile
         })
         .subscribe((token) => {
            const headers = new HttpHeaders({
               Authorization: `Bearer ${token.accessToken}`,
            })
            // use $filter startswith
            const url =
               `https://graph.microsoft.com/v1.0/users` +
               `?$select=displayName,userPrincipalName,mail` +
               `&$top=10&$filter=startswith(displayName,'${encodeURIComponent(q)}')`

            this.http.get<any>(url, { headers }).subscribe((res) => {
               const allUserSuggestions = res?.value || []
               this.userSuggestions = allUserSuggestions.filter((u) => u.userPrincipalName.toLowerCase().endsWith('@vetcor.com'))
            })
         })
   }

   async signIn(): Promise<void> {
      await this.msal.instance.initialize()

      this.msal
         .loginPopup({ scopes: ['User.Read', 'Calendars.ReadWrite', 'OnlineMeetings.ReadWrite'], prompt: 'select_account' })
         .subscribe((response: AuthenticationResult) => {
            this.msal.instance.setActiveAccount(response.account)
            this.isSignedIn = true

            //use the signed-in AD user for the captain user's email
            if (this.captainUser) {
               this.attendeeUsers = [
                  {
                     displayName: response.account?.name || '',
                     userPrincipalName: response.account?.username || '',
                     mail: response.account?.username || '',
                  },
               ]
            }
         })
   }

   async scheduleMeeting(): Promise<void> {
      await this.msal.instance.initialize()

      const account = this.msal.instance.getActiveAccount()
      if (!account) {
         return
      }

      if (!this.currentUser && !this.attendeeUsers) {
         console.log('Could not schedule')

         console.log('currentUser: ')
         console.log(this.currentUser)

         console.log('attendeeUsers: ')
         console.log(this.attendeeUsers)
         return
      }

      this.msal
         .acquireTokenSilent({
            account,
            scopes: ['User.Read', 'Calendars.ReadWrite', 'User.ReadBasic.All', 'OnlineMeetings.ReadWrite'],
         })
         .subscribe((tokenResponse) => {
            const accessToken = tokenResponse.accessToken
            this.loading = true

            const safeDate = this.formatDateForGraph(this.meetingDate!)
            const [year, month, day] = safeDate.split('-').map((n) => parseInt(n, 10))
            const [hours, minutes] = this.startTime.split(':').map((n) => parseInt(n, 10))

            const pad = (n: number) => n.toString().padStart(2, '0')

            const startStr = `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`

            const endDate = new Date(year, month - 1, day, hours, minutes + this.duration)
            const endStr = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}T${pad(endDate.getHours())}:${pad(
               endDate.getMinutes()
            )}:00`

            const attendees = this.attendeeUsers.map((user) => ({
               emailAddress: {
                  address: user.userPrincipalName,
                  name: user.displayName,
               },
               type: 'required',
            }))

            const body = {
               subject: this.subject,
               start: { dateTime: startStr, timeZone: this.selectedTimeZone },
               end: { dateTime: endStr, timeZone: this.selectedTimeZone },
               attendees: attendees,
               isOnlineMeeting: true,
               onlineMeetingProvider: 'teamsForBusiness',
            }

            this.http
               .post('https://graph.microsoft.com/v1.0/me/events?sendNotifications=true', body, {
                  headers: new HttpHeaders({
                     Authorization: `Bearer ${accessToken}`,
                     'Content-Type': 'application/json',
                  }),
               })
               .subscribe({
                  next: (res: any) => {
                     console.log('Success', res)
                     this.result = {
                        ...res,
                        currentUser: this.currentUser,
                     }

                     this.loading = false

                     this.meetingCreated.emit(this.result)
                  },
                  error: (err) => {
                     console.error('Error', err)
                     this.loading = false
                  },
               })
         })
   }

   get minDate(): Date | null {
      if (!this.allowedStartDate) return null
      const d = new Date(this.allowedStartDate)
      d.setHours(0, 0, 0, 0)
      return d
   }

   get maxDate(): Date | null {
      if (!this.allowedEndDate) return null
      const d = new Date(this.allowedEndDate)
      d.setHours(23, 59, 59, 999)
      return d
   }

   onAttendeeChanged(newAttendees: any[]) {
      this.attendeeUsers = newAttendees
      this.availableTimes = [] // clear stale slots
      this.startTime = ''

      if (this.meetingDate) {
         this.getAvailableTimes(this.formatDateForGraph(this.meetingDate!))
      }
   }

   onDateSelected(newDate: Date) {
      if (!newDate) return

      this.meetingDate = newDate

      const safeDate = this.formatDateForGraph(newDate)

      this.getAvailableTimes(safeDate)
   }

   private formatDateForGraph(date: Date): string {
      const yyyy = date.getFullYear()
      const mm = (date.getMonth() + 1).toString().padStart(2, '0')
      const dd = date.getDate().toString().padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
   }

   getAvailableTimes(date: string) {
      const account = this.msal.instance.getActiveAccount()
      if (!account || !this.attendeeUsers || this.attendeeUsers.length === 0) return

      this.msal
         .acquireTokenSilent({
            account,
            scopes: ['Calendars.ReadWrite.Shared'],
         })
         .subscribe((tokenResponse) => {
            const accessToken = tokenResponse.accessToken

            const startOfDay = new Date(date)
            if (this.selectedTimeZone == 'Eastern Standard Time') {
               startOfDay.setHours(8, 0, 0, 0)
            } else if (this.selectedTimeZone == 'Central Standard Time') {
               startOfDay.setHours(9, 0, 0, 0)
            } else if (this.selectedTimeZone == 'Mountain Standard Time') {
               startOfDay.setHours(10, 0, 0, 0)
            } else if (this.selectedTimeZone == 'Pacific Standard Time') {
               startOfDay.setHours(11, 0, 0, 0)
            } else if (this.selectedTimeZone == 'Hawaiian Standard Time') {
               startOfDay.setHours(13, 0, 0, 0)
            }

            const body = {
               schedules: [...this.attendeeUsers.map((u) => u.userPrincipalName), this.currentUser?.workEmail],
               startTime: {
                  dateTime:
                     this.selectedTimeZone == 'Eastern Standard Time'
                        ? `${date}T08:00:00`
                        : this.selectedTimeZone == 'Central Standard Time'
                          ? `${date}T09:00:00`
                          : this.selectedTimeZone == 'Mountain Standard Time'
                            ? `${date}T10:00:00`
                            : this.selectedTimeZone == 'Pacific Standard Time'
                              ? `${date}T11:00:00`
                              : this.selectedTimeZone == 'Hawaiian Standard Time'
                                ? `${date}T13:00:00`
                                : `${date}T08:00:00`,
                  timeZone: this.selectedTimeZone,
               },
               endTime: {
                  dateTime:
                     this.selectedTimeZone == 'Eastern Standard Time'
                        ? `${date}T16:30:00`
                        : this.selectedTimeZone == 'Central Standard Time'
                          ? `${date}T17:30:00`
                          : this.selectedTimeZone == 'Mountain Standard Time'
                            ? `${date}T18:30:00`
                            : this.selectedTimeZone == 'Pacific Standard Time'
                              ? `${date}T19:30:00`
                              : this.selectedTimeZone == 'Hawaiian Standard Time'
                                ? `${date}T21:30:00`
                                : `${date}T17:30:00`,
                  timeZone: this.selectedTimeZone,
               },
               availabilityViewInterval: this.duration,
            }

            this.http
               .post('https://graph.microsoft.com/v1.0/me/calendar/getSchedule', body, {
                  headers: new HttpHeaders({
                     Authorization: `Bearer ${accessToken}`,
                     'Content-Type': 'application/json',
                     Prefer: `outlook.timezone="${this.selectedTimeZone}"`,
                  }),
               })
               .subscribe({
                  next: (res: any) => {
                     const values = res?.value || []
                     if (values.length === 0) {
                        this.availableTimes = []
                        return
                     }

                     const validViews = values.map((v: any) => v.availabilityView).filter((view: string) => view && view.length > 0)

                     if (validViews.length === 0) {
                        console.warn('No valid availability data returned')
                        this.availableTimes = []
                        return
                     }

                     // Build merged view from valid schedules only
                     let mergedView = ''
                     for (let i = 0; i < validViews[0].length; i++) {
                        const slotCodes = validViews.map((v) => v[i])
                        mergedView += slotCodes.every((c) => c === '0') ? '0' : '1'
                     }

                     // Build slots from merged availability
                     this.availableTimes = this.buildFreeSlots(startOfDay, mergedView)
                  },
                  error: (err) => console.error('Error fetching schedule', err),
               })
         })
   }

   buildFreeSlots(start: Date, availability: string): { label: string; value: string; disabled: boolean }[] {
      const slots: { label: string; value: string; disabled: boolean }[] = []
      let cursor = new Date(start)
      const ianaTz = this.windowsToIanaMap[this.selectedTimeZone] || 'UTC'

      for (let i = 0; i < availability.length; i++) {
         const next = new Date(cursor.getTime() + this.duration * 60000)

         // availabilityView codes: 0 = free, 1 = tentative, 2 = busy, 3 = OOF
         const code = availability[i]
         const disabled = code !== '0'

         // Format value in the selected timezone (not local time)
         const valueParts = new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: ianaTz,
         }).formatToParts(cursor)
         const hourPart = valueParts.find((p) => p.type === 'hour')?.value || '00'
         const minutePart = valueParts.find((p) => p.type === 'minute')?.value || '00'
         const value = `${hourPart}:${minutePart}`

         const formatter = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: ianaTz,
         })
         const label = formatter.format(cursor)

         slots.push({ label, value, disabled })
         cursor = next
      }

      return slots
   }

   onTimeZoneChanged(newZone: string) {
      this.selectedTimeZone = newZone
      if (this.meetingDate) {
         this.getAvailableTimes(this.formatDateForGraph(this.meetingDate!))
      }
   }

   onDurationChanged() {
      this.startTime = ''
      if (this.meetingDate && this.attendeeUsers) {
         this.getAvailableTimes(this.formatDateForGraph(this.meetingDate!))
      }
   }
}
